import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../../config'

const MAX_MB = 50

// Admin dialog: upload an image or a document to Circular/Notices/Minutes.
// Uses the same upload + post APIs as Designs (image -> /post/Updates) and
// Project management details (document -> /post/Document). Data.Section = 'notice'
// keeps the post out of those two pages, so it shows only here.
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
const DOC_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain'
]
const Uploadnoticemodal = ({ show, onClose, onCreated }) => {
  const [title, setTitle] = useState('')
  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    if (show) {
      setTitle('')
      setFile(null)
      setErrors({})
    }
  }, [show])

  const pickFile = e => {
    const f = e.target.files?.[0] || null
    setErrors(prev => ({ ...prev, file: undefined }))
    if (f && ![...IMAGE_TYPES, ...DOC_TYPES].includes(f.type)) {
      setErrors(prev => ({ ...prev, file: 'Only images (JPG, PNG) and documents (PDF, Word, Excel, text) are allowed' }))
      setFile(null)
      e.target.value = ''
      return
    }
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setErrors(prev => ({ ...prev, file: `File is too large (max ${MAX_MB} MB)` }))
      setFile(null)
      e.target.value = ''
      return
    }
    setFile(f)
  }

  const submit = async ev => {
    ev.preventDefault()
    const errs = {}
    if (!title.trim()) errs.title = 'Enter a title'
    if (!file) errs.file = 'Choose a file to upload'
    setErrors(errs)
    if (Object.keys(errs).length) return

    setSaving(true)
    const headers = { Authorization: localStorage.getItem('token') }
    try {
      const formData = new FormData()
      formData.append('upload', file)
      const up = await axios.post(`${config.apiUrl}/upload/image`, formData, { headers })
      const { message, status, ...link } = up.data || {}
      if (!link.data) throw new Error('Upload returned no file link')

      const isImage = IMAGE_TYPES.includes(file.type)
      await axios.post(
        `${config.apiUrl}/post/${isImage ? 'Updates' : 'Document'}`,
        {
          Data: isImage
            ? { Title: title.trim(), ImageLink: link, MediaType: 'image', Section: 'notice' }
            : { Title: title.trim(), DocumentLink: link, Section: 'notice' }
        },
        { headers }
      )
      toast.success('Uploaded')
      onCreated && onCreated()
      onClose()
    } catch (err) {
      console.error('Notice upload failed', err)
      toast.error(err.response?.data?.message || 'Upload failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={saving ? () => {} : onClose} centered backdrop="static" aria-labelledby="notice-upload-title">
      <form onSubmit={submit} noValidate>
        <Modal.Header closeButton closeLabel="Close">
          <Modal.Title as="h2" id="notice-upload-title" className="h3 mb-0">Upload image or document</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label" htmlFor="notice-title">Title <span className="text-danger" aria-hidden="true">*</span></label>
            <input
              id="notice-title"
              type="text"
              maxLength={1000}
              className={`form-control${errors.title ? ' is-invalid' : ''}`}
              value={title}
              onChange={e => { setTitle(e.target.value); setErrors(p => ({ ...p, title: undefined })) }}
              placeholder="e.g. Minutes of general meeting, Oct 2026"
            />
            {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
          </div>

          <div className="mb-1">
            <label className="form-label" htmlFor="notice-file">File <span className="text-danger" aria-hidden="true">*</span></label>
            <input
              id="notice-file"
              ref={fileRef}
              type="file"
              className={`form-control${errors.file ? ' is-invalid' : ''}`}
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={pickFile}
            />
            <div className="form-text">Image (JPG, PNG) or document (PDF, Word, Excel, text). Max {MAX_MB} MB.</div>
            {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default Uploadnoticemodal
